import { useState } from 'react';
import { CalendarCheck, FilePlus2, TriangleAlert, UserRoundPen } from 'lucide-react';
import { BoutonPrincipal, Pastille } from '../ui/Primitives';
import { useToast } from '../ui/useToast';
import { ErreurApi } from '../../lib/api';
import { detailsRefus } from '../../lib/erreurs-api';
import { usePreparerFactureFinParcours } from '../../lib/useFactures';
import type { FacturationFinParcours as Facturation } from '../../lib/useFinancements';

function messageRefus(erreur: unknown, defaut: string): string {
  const detail = detailsRefus(erreur)[0];
  if (detail) return detail.message;
  return erreur instanceof ErreurApi ? erreur.message : defaut;
}

/**
 * Facture unique de fin de parcours d'un accord France Travail (TF-418) : le
 * parcours, ses heures réalisées — déclarées par les formateurs (TF-421),
 * émargées avant leur mise en service — avec le repère émargé et qui les a
 * déclarées, ce qui est facturé et ce qui reste à facturer, et le geste qui
 * prépare la facture, ou son complément. Le cumul se lit face au volume de
 * l'accord. Tout — état, montants, refus — vient de l'API.
 */
export function FacturationFinParcours({
  accordId,
  facturation,
}: {
  accordId: string;
  facturation: Facturation;
}): JSX.Element {
  const toast = useToast();
  const preparer = usePreparerFactureFinParcours();
  const [erreur, setErreur] = useState<string | null>(null);
  const { cumul } = facturation;
  const refus = facturation.preparation?.refus ?? null;
  const complement = facturation.etat === 'COMPLEMENT';

  const lancer = (): void => {
    setErreur(null);
    preparer.mutate(accordId, {
      onSuccess: () =>
        toast(
          `Facture ${complement ? 'complémentaire ' : ''}de fin de parcours préparée : vérifiez-la avant de l’émettre.`,
        ),
      onError: (motif) => setErreur(messageRefus(motif, 'La facture n’a pas pu être préparée.')),
    });
  };

  return (
    <section className="rounded-2xl border border-gray-100" aria-label="Facture de fin de parcours">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-100 px-3 py-2.5 sm:px-4">
        <h5 className="flex min-w-0 items-center gap-2 text-sm font-bold text-codialis-navy">
          <CalendarCheck className="h-4 w-4 shrink-0 text-codialis-sky" aria-hidden />
          Facture de fin de parcours
        </h5>
        <span className="text-[11px] text-codialis-gray">Heures : {facturation.source}</span>
      </div>

      <div className="space-y-3 p-3 sm:p-4">
        <div className="rounded-xl bg-codialis-sand px-3 py-2.5 sm:px-4">
          <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
            <p className="text-sm font-semibold text-codialis-navy">
              {cumul.heuresFacturees} facturées
              {cumul.volume ? <span className="font-normal text-codialis-gray"> sur {cumul.volume}</span> : null}
            </p>
            <p className="text-sm font-semibold tabular-nums text-codialis-navy">{cumul.montantFacture}</p>
          </div>
          {cumul.part !== null && (
            <div
              className="mt-2 h-2 overflow-hidden rounded-full bg-white"
              role="progressbar"
              aria-label="Part du volume de l’accord facturée"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={cumul.part}
            >
              <div className="h-full rounded-full bg-codialis-sky" style={{ width: `${cumul.part}%` }} />
            </div>
          )}
          {cumul.reste && <p className="mt-1 text-[11px] text-codialis-gray">Reste {cumul.reste} dans le volume de l’accord.</p>}
        </div>

        <div className="min-w-0 rounded-xl border border-gray-100 bg-white px-3 py-2.5 sm:px-4">
          <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-1.5">
            <div className="min-w-0 flex-1 basis-40">
              <p className="text-sm font-bold text-codialis-navy">Parcours</p>
              {facturation.periode && <p className="text-[11px] text-codialis-gray">{facturation.periode}</p>}
            </div>
            <Pastille texte={facturation.etatLibelle} ton={facturation.ton} />
          </div>

          <dl className="mt-2 grid grid-cols-3 gap-2 text-xs">
            <div className="min-w-0">
              <dt className="text-[11px] text-codialis-gray">Réalisées</dt>
              <dd className="font-semibold tabular-nums text-codialis-navy">
                {facturation.heuresRealisees}
                {facturation.heuresEmargees && (
                  <span className="block font-normal text-codialis-gray">{facturation.heuresEmargees} émargées</span>
                )}
              </dd>
            </div>
            <div className="min-w-0">
              <dt className="text-[11px] text-codialis-gray">Facturées</dt>
              <dd className="font-semibold tabular-nums text-codialis-navy">
                {facturation.facture ? (
                  <>
                    {facturation.facture.heures}
                    <span className="block font-normal text-codialis-gray">{facturation.facture.montant}</span>
                  </>
                ) : (
                  '—'
                )}
              </dd>
            </div>
            <div className="min-w-0">
              <dt className="text-[11px] text-codialis-gray">À facturer</dt>
              <dd className="font-semibold tabular-nums text-codialis-navy">
                {facturation.aFacturer ? (
                  <>
                    {facturation.aFacturer.heures}
                    {facturation.aFacturer.montant && (
                      <span className="block font-normal text-codialis-gray">{facturation.aFacturer.montant}</span>
                    )}
                  </>
                ) : (
                  '—'
                )}
              </dd>
            </div>
          </dl>

          {facturation.facture &&
            facturation.facture.numeros.length > 0 &&
            !facturation.detail?.includes(facturation.facture.numeros[0] ?? '') && (
              <p className="mt-1 break-words text-[11px] text-codialis-gray">
                <span className="font-mono">{facturation.facture.numeros.join(', ')}</span>
              </p>
            )}
          {facturation.origine && (
            <p className="mt-1 flex gap-1.5 text-[11px] text-codialis-gray">
              <UserRoundPen className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden />
              <span className="min-w-0 break-words">{facturation.origine}</span>
            </p>
          )}
          {facturation.declarationsAttendues && facturation.etat !== 'EN_ATTENTE_DECLARATION' && (
            <p className="mt-1 flex gap-1.5 text-[11px] font-semibold text-amber-700">
              <TriangleAlert className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden />
              <span className="min-w-0 break-words">{facturation.declarationsAttendues}</span>
            </p>
          )}
          {facturation.feuillesOuvertes && facturation.etat !== 'EN_ATTENTE_CLOTURE' && (
            <p className="mt-1 flex gap-1.5 text-[11px] font-semibold text-amber-700">
              <TriangleAlert className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden />
              <span className="min-w-0">{facturation.feuillesOuvertes} : leurs heures ne sont pas encore comptées.</span>
            </p>
          )}
          {facturation.detail && (
            <p
              className={`mt-1 break-words text-xs ${
                facturation.etat === 'EN_ATTENTE_CLOTURE' || facturation.etat === 'EN_ATTENTE_DECLARATION'
                  ? 'font-semibold text-amber-700'
                  : 'text-codialis-gray'
              }`}
            >
              {facturation.detail}
            </p>
          )}

          {facturation.preparation && (
            <div className="mt-2 flex flex-wrap items-center justify-end gap-x-3 gap-y-1.5">
              {refus && <p className="min-w-0 flex-1 text-xs text-codialis-gray">{refus}</p>}
              <BoutonPrincipal onClick={lancer} disabled={refus !== null || preparer.isPending}>
                <FilePlus2 className="mr-1.5 h-4 w-4" aria-hidden />
                {preparer.isPending ? 'Préparation…' : complement ? 'Préparer le complément' : 'Préparer la facture'}
              </BoutonPrincipal>
            </div>
          )}
          {erreur && (
            <p role="alert" className="mt-2 rounded-lg bg-red-50 px-2.5 py-1.5 text-xs font-semibold text-red-700">
              {erreur}
            </p>
          )}
        </div>
      </div>
    </section>
  );
}
